import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera, Video, CheckCircle2, Trash2, Upload, Loader2, X, ShieldCheck, ShieldAlert, Lock } from "lucide-react";
import { CATEGORIES, ABIDJAN_QUARTIERS, SUBCATEGORIES, SUBCATEGORY_HINTS, ENERGIES, BOITES, VEHICULE_MARQUES, MOTO_MARQUES, MOTO_ENERGIES, CYLINDREES, isMoto, fmtPrice, saveDraft, loadDraft, clearDraft } from "@/lib/utils";
import { useCountry } from "@/lib/CountryContext";
import { createAnnonce, uploadToCloudinary } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import PhoneOtpModal from "@/components/PhoneOtpModal";
import AiBoosterButton from "@/components/AiBoosterButton";
import PriceCoach from "@/components/PriceCoach";
import { EQUIPEMENTS_CATALOG, EQUIPEMENT_KEYS } from "@/components/EquipementsBadges";

const STEPS = ["Categorie", "Details", "Photos", "Confirmation"];

const empty = {
  category: "", subcategory: "", title: "", city: "", quartier: "",
  price: "", price_unit: "", phone: "", whatsapp: "",
  description: "", image: "", images: [], accepted: false,
  // Phase 2 Immobilier — structured fields
  type_bien: "", statut: "", etat_bien: "",
  documents_terrain: "", usage_terrain: "", surface_m2: "",
  equipements: [],  // list of equipment keys (see EquipementsBadges catalog)
  energie: "",      // Vehicules — type d'energie
  cylindree: "",    // Motos — 50 | 125 | 250+
  marque: "",
  annee: "",
  kilometrage: "",
  boite: "",
};

export default function DeposerPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const navigate = useNavigate();
  const { user, login, loading: authLoading } = useAuth();
  const { code: countryCode, country } = useCountry();
  const hasPlan = !!user && (user.is_pro || user.plan === "free" || user.plan === "premium");
  const maxPhotos = user?.is_pro ? 6 : 2;
  const gateOk = !authLoading && !!user && (user.is_admin || (user.phone_verified === true && hasPlan));

  // Funnel gate: login -> SMS verified -> plan chosen -> annonce. Anonymous or unverified users are redirected.
  useEffect(() => {
    if (authLoading) return;
    if (!user || (!user.is_admin && (user.phone_verified !== true || !hasPlan))) {
      navigate("/choisir-abonnement?next=%2Fdeposer", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasPlan, authLoading]);

  // Load draft once + resume on the right step (TTL 6 h + user-scoped).
  // We wait until the user object is resolved (or confirmed null) so the
  // draft of another user on the same device is never restored.
  useEffect(() => {
    // While auth is still loading, `user` may be null briefly — wait one tick
    // before touching the draft store. We use a sentinel `draftLoaded` flag.
    if (draftLoaded || !gateOk) return;
    const d = loadDraft(user?.user_id || null);
    if (d) {
      // Strip our private metadata before pushing to form state
      const { _saved_at_ms: _ts, _user_id: _uid, ...clean } = d;
      void _ts; void _uid;
      setForm({ ...empty, ...clean });
      if (clean.accepted) setStep(3);
      else if (clean.title && clean.city && clean.price && clean.description) setStep(2);
      else if (clean.category) setStep(1);
      setDraftSavedAt(d._saved_at_ms || null);
    }
    setDraftLoaded(true);
  }, [user, draftLoaded, gateOk]);

  // Auto-save draft on every form change (scoped to the current user)
  useEffect(() => {
    if (!draftLoaded) return; // don't overwrite the just-restored draft
    saveDraft(form, user?.user_id || null);
  }, [form, draftLoaded, user]);

  const startFresh = () => {
    clearDraft();
    setForm(empty);
    setStep(0);
    setDraftSavedAt(null);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Memoize the flattened, deduped sub-category list per chosen category
  const subOptions = useMemo(() => {
    const tree = SUBCATEGORIES[form.category];
    if (!tree) return [];
    return Object.values(tree).flat().filter((v, i, a) => a.indexOf(v) === i);
  }, [form.category]);

  const canContinue = () => {
    if (step === 0) return !!form.category;
    if (step === 1) return form.title && form.city && form.price && form.description;
    if (step === 2) return true;
    if (step === 3) return form.accepted;
    return true;
  };

  const publier = async (opts = {}) => {
    // Strict-mode for Côte d'Ivoire: phone must be verified before publishing.
    // `opts.skipVerifyCheck` is used after a successful OTP verification (the
    // useAuth() user is updated asynchronously, so we trust the OTP modal's
    // confirmation and let the backend be the final gate).
    if (!opts.skipVerifyCheck && (countryCode || "CI").toUpperCase() === "CI" && user && user.phone_verified !== true) {
      setOtpOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const created = await createAnnonce({
        title: form.title,
        category: form.category,
        subcategory: form.subcategory,
        city: form.city,
        quartier: form.quartier,
        country: countryCode,
        price: parseInt(form.price || "0", 10),
        price_unit: form.price_unit,
        description: form.description,
        image: form.image,
        images: form.images,
        phone: form.phone,
        whatsapp: form.whatsapp,
        // Phase 2 Immobilier
        type_bien: form.type_bien,
        statut: form.statut,
        etat_bien: form.etat_bien,
        documents_terrain: form.documents_terrain,
        usage_terrain: form.usage_terrain,
        surface_m2: form.surface_m2 ? parseInt(form.surface_m2, 10) : null,
        equipements: form.equipements || [],
        energie: form.category === "Vehicules" ? form.energie : "",
        marque: form.category === "Vehicules" ? form.marque : "",
        annee: form.category === "Vehicules" && form.annee ? Number(form.annee) : null,
        kilometrage: form.category === "Vehicules" && form.kilometrage !== "" ? Number(form.kilometrage) : null,
        boite: form.category === "Vehicules" ? form.boite : "",
        cylindree: form.category === "Vehicules" && isMoto(form.subcategory) ? form.cylindree : "",
      });
      clearDraft();
      navigate(`/annonces/${created.id}`);
    } catch (err) {
      // Backend strict gate: open the OTP modal if the user isn't verified yet
      if (err?.response?.status === 403 && err.response?.data?.detail === "phone_verification_required") {
        setOtpOpen(true);
      } else if (err?.response?.status === 402) {
        const d = err.response?.data?.detail;
        const code = typeof d === "object" ? d.code : "";
        navigate(code === "plan_required" ? "/choisir-abonnement?next=%2Fdeposer" : "/choisir-abonnement?next=%2Fdeposer#premium");
        throw new Error(typeof d === "object" ? d.message : d);
      } else {
        throw err;
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { clearDraft(); setForm(empty); setStep(0); setDraftLoaded(true); setDraftSavedAt(null); };

  if (!gateOk) {
    return (
      <main className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="deposer-gate-loading">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
        <p className="text-sm text-[var(--ink-soft)] mt-4">Vérification de votre compte…</p>
      </main>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-6 py-10" data-testid="deposer-page">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Deposer une annonce</h1>
        {draftSavedAt && (
          <span className="badge-verified" data-testid="draft-badge">Brouillon restaure</span>
        )}
      </div>
      <p className="text-[var(--ink-soft)] mb-3">4 etapes simples. Votre brouillon est automatiquement sauvegarde sur ce navigateur (6h).</p>

      {draftSavedAt && (form.title || form.image || form.images?.length > 0) && (
        <div className="glass-card p-3 mb-6 flex items-start gap-3 bg-amber-50 border-amber-200" data-testid="draft-resume-banner">
          <div className="text-2xl">📝</div>
          <div className="flex-1 text-sm">
            <div className="font-semibold">
              Brouillon precedent restaure
              {form.title && <> : <span className="text-orange-700">&quot;{form.title}&quot;</span></>}
            </div>
            <div className="text-xs text-[var(--ink-soft)] mt-0.5">
              Sauvegarde le {new Date(draftSavedAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}.
              Continuez-le ou recommencez a zero.
            </div>
          </div>
          <button
            type="button"
            onClick={startFresh}
            className="btn-secondary !py-1 !px-3 text-xs whitespace-nowrap"
            data-testid="draft-start-fresh-btn"
          >
            Nouvelle annonce vide
          </button>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center mb-8" data-testid="stepper">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center" style={{ flex: i === STEPS.length - 1 ? "0 0 auto" : "1 1 auto" }}>
            <div className={`step-dot ${i <= step ? "active" : ""}`} data-testid={`step-dot-${i}`}>{i + 1}</div>
            <div className="ml-2 mr-3 text-sm font-semibold whitespace-nowrap" style={{ color: i <= step ? "var(--ink)" : "var(--ink-soft)" }}>
              {label}
            </div>
            {i < STEPS.length - 1 && <div className={`step-line ${i < step ? "active" : ""}`} />}
          </div>
        ))}
      </div>

      <div className="glass-card p-8" data-testid="deposer-card">
        {/* Step 1 - Categorie */}
        {step === 0 && (
          <div data-testid="step-categorie">
            <h2 className="font-bold text-xl mb-2">Choisissez une rubrique</h2>
            <p className="text-sm text-[var(--ink-soft)] mb-5">La rubrique determine les champs proposes a l&apos;etape suivante.</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => set("category", c.key)}
                  className={`chip ${form.category === c.key ? "active" : ""}`}
                  data-testid={`deposer-cat-${c.key}`}
                >
                  {c.key}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 - Details */}
        {step === 1 && (
          <div data-testid="step-details">
            <h2 className="font-bold text-xl mb-5">Details de l&apos;annonce</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label>Titre</label>
                <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Villa moderne 5 pieces..." data-testid="form-title" />
                <AiBoosterButton kind="title" data={form} isPro={!!user?.is_pro} onApply={(v) => set("title", v)} />
              </div>
              {subOptions.length > 0 && (
                <div className="md:col-span-2">
                  <label>Sous-categorie</label>
                  <div className="flex flex-wrap gap-2">
                    {subOptions.map((sub) => (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => set("subcategory", sub)}
                        className={`chip ${form.subcategory === sub ? "active" : ""}`}
                        data-testid={`form-sub-${sub}`}
                        title={SUBCATEGORY_HINTS[sub] || ""}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                  {form.subcategory && SUBCATEGORY_HINTS[form.subcategory] && (
                    <p className="text-xs text-[var(--ink-soft)] mt-2" data-testid="subcat-hint">
                      <span className="font-semibold text-orange-600">{form.subcategory} :</span> {SUBCATEGORY_HINTS[form.subcategory]}
                    </p>
                  )}
                </div>
              )}
              {form.category === "Immobilier" && (
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-orange-50/50 border border-orange-200 rounded-2xl" data-testid="immo-extra-fields">
                  <div>
                    <label>Type de bien</label>
                    <select value={form.type_bien} onChange={(e) => set("type_bien", e.target.value)} data-testid="form-type-bien">
                      <option value="">Choisir...</option>
                      <option value="maison">Maison / Villa</option>
                      <option value="appartement">Appartement / Studio</option>
                      <option value="terrain">Terrain / Parcelle</option>
                    </select>
                  </div>
                  <div>
                    <label>Statut</label>
                    <select value={form.statut} onChange={(e) => set("statut", e.target.value)} data-testid="form-statut">
                      <option value="">Choisir...</option>
                      <option value="vente">A vendre</option>
                      <option value="location">A louer</option>
                    </select>
                  </div>
                  {form.type_bien && form.type_bien !== "terrain" && (
                    <div className="sm:col-span-2">
                      <label>Etat du bien</label>
                      <select value={form.etat_bien} onChange={(e) => set("etat_bien", e.target.value)} data-testid="form-etat-bien">
                        <option value="">Choisir...</option>
                        <option value="sans_travaux">Sans travaux (pret a habiter)</option>
                        <option value="avec_travaux">Avec travaux (a renover)</option>
                        <option value="inachevee">Inachevee (chantier a finir)</option>
                      </select>
                    </div>
                  )}
                  {form.type_bien === "terrain" && form.statut === "vente" && (
                    <div className="sm:col-span-2">
                      <label>Documents fonciers</label>
                      <select value={form.documents_terrain} onChange={(e) => set("documents_terrain", e.target.value)} data-testid="form-documents-terrain">
                        <option value="">Choisir...</option>
                        <option value="avec_document">Avec documents (titre / ACD)</option>
                        <option value="sans_document">Sans documents officiels</option>
                      </select>
                    </div>
                  )}
                  {form.type_bien === "terrain" && form.statut === "location" && (
                    <div className="sm:col-span-2">
                      <label>Usage du terrain</label>
                      <select value={form.usage_terrain} onChange={(e) => set("usage_terrain", e.target.value)} data-testid="form-usage-terrain">
                        <option value="">Choisir...</option>
                        <option value="ville">Terrain de ville</option>
                        <option value="agricole">Terrain agricole / exploitation</option>
                      </select>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label>Surface (m²) — optionnel</label>
                    <input
                      type="number" min="0" inputMode="numeric"
                      value={form.surface_m2}
                      onChange={(e) => set("surface_m2", e.target.value)}
                      placeholder="Ex : 350"
                      data-testid="form-surface-m2"
                    />
                    {form.surface_m2 && form.price && (
                      <p className="text-xs text-orange-700 mt-1 font-semibold" data-testid="price-per-m2-hint">
                        Soit ~{Math.round(parseInt(form.price, 10) / parseInt(form.surface_m2, 10)).toLocaleString("fr-FR")} FCFA/m²
                      </p>
                    )}
                  </div>
                  {/* Equipments — only when it makes sense (furnished / location courte durée) */}
                  {form.statut === "location" && form.type_bien !== "terrain" && (
                    <div className="sm:col-span-2 mt-2">
                      <label className="block text-sm font-semibold mb-2">
                        Equipements <span className="text-[var(--ink-soft)] font-normal">(optionnel — pour location meublee)</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="form-equipements">
                        {EQUIPEMENT_KEYS.map((k) => {
                          const { icon: Icon, label } = EQUIPEMENTS_CATALOG[k];
                          const selected = (form.equipements || []).includes(k);
                          return (
                            <button
                              type="button"
                              key={k}
                              onClick={() => {
                                const cur = new Set(form.equipements || []);
                                if (selected) cur.delete(k); else cur.add(k);
                                set("equipements", Array.from(cur));
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition ${
                                selected
                                  ? "bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold"
                                  : "bg-white border-[var(--line)] text-[var(--ink-soft)] hover:border-emerald-300"
                              }`}
                              data-testid={`form-equip-${k}`}
                            >
                              <Icon size={14} />
                              <span className="truncate">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                      {form.equipements && form.equipements.length > 0 && (
                        <p className="text-xs text-emerald-700 mt-2 font-medium">
                          {form.equipements.length} equipement{form.equipements.length > 1 ? "s" : ""} selectionne{form.equipements.length > 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {form.category === "Vehicules" && (
                <div className="md:col-span-2 p-4 bg-orange-50/50 border border-orange-200 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vehicule-extra-fields">
                  <div>
                    <label>{isMoto(form.subcategory) ? "Fabricant de la moto" : "Marque"}</label>
                    <select value={form.marque} onChange={(e) => set("marque", e.target.value)} data-testid="form-marque">
                      <option value="">Choisir...</option>
                      {(isMoto(form.subcategory) ? MOTO_MARQUES : VEHICULE_MARQUES).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label>{isMoto(form.subcategory) ? "Carburant" : "Type d'energie"}</label>
                    <select value={form.energie} onChange={(e) => set("energie", e.target.value)} data-testid="form-energie">
                      <option value="">Choisir...</option>
                      {(isMoto(form.subcategory) ? MOTO_ENERGIES : ENERGIES).map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
                    </select>
                  </div>
                  {isMoto(form.subcategory) && (
                    <div>
                      <label>Cylindree</label>
                      <select value={form.cylindree} onChange={(e) => set("cylindree", e.target.value)} data-testid="form-cylindree">
                        <option value="">Choisir...</option>
                        {CYLINDREES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label>Annee</label>
                    <input type="number" inputMode="numeric" min="1980" max={new Date().getFullYear() + 1} value={form.annee} onChange={(e) => set("annee", e.target.value)} placeholder="Ex : 2018" data-testid="form-annee" />
                  </div>
                  <div>
                    <label>Kilometrage (km)</label>
                    <input type="number" inputMode="numeric" min="0" step="1000" value={form.kilometrage} onChange={(e) => set("kilometrage", e.target.value)} placeholder="Ex : 85000" data-testid="form-kilometrage" />
                  </div>
                  <div>
                    <label>{isMoto(form.subcategory) ? "Transmission" : "Boite de vitesses"}</label>
                    <select value={form.boite} onChange={(e) => set("boite", e.target.value)} data-testid="form-boite">
                      <option value="">Choisir...</option>
                      {BOITES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                    </select>
                  </div>
                  <p className="text-xs text-[var(--ink-soft)] md:col-span-2">Ces details aident les acheteurs a filtrer et augmentent vos chances de contact.</p>
                </div>
              )}
              <div>
                <label>Ville</label>
                <select value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="form-city">
                  <option value="">Choisir...</option>
                  {country.cities.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label>Quartier / Zone</label>
                {form.city === "Abidjan" ? (
                  <select value={form.quartier} onChange={(e) => set("quartier", e.target.value)} data-testid="form-quartier">
                    <option value="">Choisir...</option>
                    {ABIDJAN_QUARTIERS.map((q) => <option key={q}>{q}</option>)}
                  </select>
                ) : (
                  <input value={form.quartier} onChange={(e) => set("quartier", e.target.value)} placeholder="Quartier..." data-testid="form-quartier" />
                )}
              </div>
              <div>
                <label>Prix (FCFA)</label>
                <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="0" data-testid="form-price" />
                <PriceCoach data={form} isPro={!!user?.is_pro} />
              </div>
              <div>
                <label>Unite (optionnel)</label>
                <select value={form.price_unit} onChange={(e) => set("price_unit", e.target.value)} data-testid="form-price-unit">
                  <option value="">Aucune</option>
                  <option value="/ nuit">/ nuit</option>
                  <option value="/ mois">/ mois</option>
                  <option value="/ an">/ an</option>
                </select>
              </div>
              <div>
                <label>Telephone</label>
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+225 ..." data-testid="form-phone" />
              </div>
              <div>
                <label>WhatsApp</label>
                <input type="tel" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+225 ..." data-testid="form-whatsapp" />
              </div>
              <div className="md:col-span-2">
                <label>Description detaillee</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  rows={5}
                  placeholder="Decrivez votre bien en detail..."
                  data-testid="form-description"
                />
                <AiBoosterButton kind="description" data={form} isPro={!!user?.is_pro} onApply={(v) => set("description", v)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 3 - Photos */}
        {step === 2 && (
          <div data-testid="step-photos">
            <h2 className="font-bold text-xl mb-1">Photos & medias</h2>
            <p className="text-sm text-[var(--ink-soft)] mb-5">
              Ajoutez jusqu&apos;a <b>{maxPhotos} photos</b> ({user?.is_pro ? "Premium" : "Gratuit : 2 photos, 6 en Premium"}). La premiere sera l&apos;image principale.
              Formats acceptes : JPG, PNG, WEBP, HEIC. <span className="text-orange-600 font-semibold">Optimisation automatique</span> : vos photos sont compressees avant envoi pour economiser vos donnees mobiles.
            </p>

            <PhotoUploader
              form={form}
              setForm={setForm}
              maxPhotos={maxPhotos}
              isPro={!!user?.is_pro}
              uploading={uploading}
              setUploading={setUploading}
              uploadError={uploadError}
              setUploadError={setUploadError}
              user={user}
              login={login}
            />

            <div className="mt-6">
              <details className="text-sm">
                <summary className="cursor-pointer text-[var(--ink-soft)] font-semibold">Saisir l&apos;URL d&apos;une image (avance)</summary>
                <div className="mt-2">
                  <input
                    value={form.image}
                    onChange={(e) => set("image", e.target.value)}
                    placeholder="https://..."
                    data-testid="form-image-url"
                  />
                  <p className="text-xs text-[var(--ink-soft)] mt-1">
                    Utile si vous avez deja une image hebergee ailleurs. Remplace l&apos;image principale.
                  </p>
                </div>
              </details>
            </div>

            <div className="mt-6">
              <label>Video immobilier (10 s max)</label>
              <div className="border-2 border-dashed border-[var(--line)] rounded-2xl py-8 text-center text-[var(--ink-soft)] text-sm">
                <Video className="mx-auto mb-2" /> Bientot disponible (option premium)
              </div>
            </div>
          </div>
        )}

        {/* Step 4 - Confirmation */}
        {step === 3 && (
          <div data-testid="step-confirmation">
            <h2 className="font-bold text-xl mb-1">Confirmation</h2>
            <p className="text-sm text-[var(--ink-soft)] mb-5">Verifiez les informations ci-dessous avant publication.</p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><span className="text-[var(--ink-soft)]">Categorie : </span><b>{form.category}</b></div>
              {form.category === "Vehicules" && (form.marque || form.energie || form.annee || form.kilometrage || form.boite) && (
                <div data-testid="confirm-vehicule">
                  <span className="text-[var(--ink-soft)]">Vehicule : </span>
                  <b>{[form.marque, CYLINDREES.find((c) => c.key === form.cylindree)?.label, form.annee, ENERGIES.find((e) => e.key === form.energie)?.label, form.kilometrage ? `${Number(form.kilometrage).toLocaleString("fr-FR")} km` : "", BOITES.find((b) => b.key === form.boite)?.label].filter(Boolean).join(" · ")}</b>
                </div>
              )}
              <div><span className="text-[var(--ink-soft)]">Titre : </span><b>{form.title}</b></div>
              <div><span className="text-[var(--ink-soft)]">Ville : </span><b>{form.city}{form.quartier ? `, ${form.quartier}` : ""}</b></div>
              <div><span className="text-[var(--ink-soft)]">Prix : </span><b className="text-orange-600">{form.price ? fmtPrice(parseInt(form.price, 10), form.price_unit) : "-"}</b></div>
              <div><span className="text-[var(--ink-soft)]">Telephone : </span><b>{form.phone || "-"}</b></div>
              <div><span className="text-[var(--ink-soft)]">WhatsApp : </span><b>{form.whatsapp || "-"}</b></div>
              <div className="col-span-2"><span className="text-[var(--ink-soft)]">Description : </span><div className="mt-1">{form.description || "-"}</div></div>
            </div>

            <AutoVerifiedCheck form={form} />

            <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="form-accept-label">
              <input
                type="checkbox"
                className="mt-1 !w-auto"
                checked={form.accepted}
                onChange={(e) => set("accepted", e.target.checked)}
                data-testid="form-accept-checkbox"
                style={{ width: "auto" }}
              />
              <span className="text-sm">
                Je reconnais avoir lu et accepte la <a href="/cgu" className="text-orange-600 font-semibold">clause de non-responsabilite</a> de Fodindin.
              </span>
            </label>
          </div>
        )}

        {/* Nav buttons */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex gap-3">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="btn-secondary" data-testid="deposer-back-btn">← Retour</button>
            )}
            {step === 3 && (
              <button onClick={reset} className="btn-secondary inline-flex items-center gap-2" data-testid="deposer-reset-btn">
                <Trash2 size={14} /> Supprimer le brouillon
              </button>
            )}
          </div>
          <div>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canContinue()}
                className="btn-primary"
                data-testid="deposer-continue-btn"
              >
                Continuer →
              </button>
            ) : (
              <button
                onClick={publier}
                disabled={!canContinue() || submitting}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="deposer-publish-btn"
              >
                <CheckCircle2 size={16} /> {submitting ? "Publication..." : "Publier l'annonce"}
              </button>
            )}
          </div>
        </div>
      </div>

      <PhoneOtpModal
        open={otpOpen}
        onOpenChange={setOtpOpen}
        onVerified={() => { setOtpOpen(false); publier({ skipVerifyCheck: true }); }}
      />
    </main>
  );
}


function PhotoUploader({ form, setForm, uploading, setUploading, uploadError, setUploadError, user, login, maxPhotos = 2, isPro = false }) {
  const MAX_PHOTOS = maxPhotos;
  const photos = [form.image, ...(form.images || [])].filter(Boolean).slice(0, MAX_PHOTOS);

  const onFiles = async (e) => {
    if (!user) {
      setUploadError("Connectez-vous pour uploader vos photos.");
      e.target.value = "";
      return;
    }
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadError("");
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setUploadError(isPro ? `Maximum ${MAX_PHOTOS} photos.` : `Abonnement Gratuit : ${MAX_PHOTOS} photos maximum. Passez Premium pour aller jusqu'a 6 photos.`);
      e.target.value = "";
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      setUploadError(`Vous ne pouvez ajouter que ${remaining} photo(s) de plus.`);
    }
    setUploading(true);
    try {
      const urls = [];
      for (const f of toUpload) {
        if (!/^image\//.test(f.type)) {
          setUploadError(`Le fichier ${f.name} n'est pas une image.`);
          continue;
        }
        if (f.size > 15 * 1024 * 1024) {
          setUploadError(`${f.name} est trop volumineux (max 15 Mo avant compression).`);
          continue;
        }
        const url = await uploadToCloudinary(f);
        urls.push(url);
      }
      setForm((prev) => {
        let nextImage = prev.image;
        const nextGallery = [...(prev.images || [])];
        for (const u of urls) {
          if (!nextImage) nextImage = u;
          else nextGallery.push(u);
        }
        return { ...prev, image: nextImage, images: nextGallery };
      });
    } catch (err) {
      // Distinguish backend 401 (session expired) vs Cloudinary 401 (bad signature, rate limit, etc.)
      const backendStatus = err?.response?.status;
      if (backendStatus === 401) {
        setUploadError("Votre session a expire. Reconnectez-vous pour uploader.");
      } else if (err?.source === "cloudinary") {
        setUploadError("Echec de l'upload de l'image. Veuillez reessayer.");
      } else {
        setUploadError(err.message || "Echec de l'upload");
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = (idx) => {
    setForm((prev) => {
      const all = [prev.image, ...(prev.images || [])].filter(Boolean);
      all.splice(idx, 1);
      const [first, ...rest] = all;
      return { ...prev, image: first || "", images: rest };
    });
  };

  const makeMain = (idx) => {
    if (idx === 0) return;
    setForm((prev) => {
      const all = [prev.image, ...(prev.images || [])].filter(Boolean);
      const [picked] = all.splice(idx, 1);
      const reordered = [picked, ...all];
      const [first, ...rest] = reordered;
      return { ...prev, image: first || "", images: rest };
    });
  };

  return (
    <div data-testid="photo-uploader">
      {!user && (
        <div
          className="mb-4 p-4 rounded-2xl border border-orange-300 bg-orange-50/70 flex items-start gap-3"
          data-testid="photo-login-required"
        >
          <Lock size={20} className="text-orange-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-orange-700">Connectez-vous ou creez votre compte pour uploader vos photos</div>
            <p className="text-sm text-[var(--ink-soft)] mt-1">
              Email + mot de passe ou compte Google. C&apos;est gratuit et instantane, vous reprendrez l&apos;upload juste apres.
            </p>
            <button
              type="button"
              onClick={login}
              className="btn-primary mt-3 inline-flex items-center gap-2"
              data-testid="photo-login-btn"
            >
              Connexion / Inscription
            </button>
            <p className="text-xs text-[var(--ink-soft)] mt-2">
              Vous pouvez aussi <b>coller l&apos;URL d&apos;une image</b> dans le panneau avance ci-dessous.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {photos.map((url, i) => (
          <div key={`${url}-${i}`} className="relative aspect-square rounded-2xl overflow-hidden group bg-orange-50 border border-[var(--line)]">
            <img src={url} alt="" className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold bg-orange-500 text-white px-2 py-0.5 rounded-full">
                Principale
              </span>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-between p-2 gap-2 opacity-0 group-hover:opacity-100">
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => makeMain(i)}
                  className="text-[10px] font-bold bg-white text-[var(--ink)] px-2 py-1 rounded-full hover:bg-orange-100"
                  data-testid={`photo-make-main-${i}`}
                >
                  Mettre en principale
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="ml-auto w-7 h-7 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600"
                aria-label="Retirer"
                data-testid={`photo-remove-${i}`}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label
            className={`aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
              user
                ? "border-orange-300 bg-orange-50/30 cursor-pointer hover:bg-orange-50 text-orange-600"
                : "border-[var(--line)] bg-gray-50 text-[var(--ink-soft)] cursor-not-allowed opacity-70"
            }`}
            data-testid="photo-add"
            title={user ? "" : "Connectez-vous pour uploader"}
          >
            {uploading ? (
              <>
                <Loader2 size={22} className="animate-spin" />
                <div className="text-xs font-semibold">Upload...</div>
              </>
            ) : !user ? (
              <>
                <Lock size={20} />
                <div className="text-xs font-bold uppercase tracking-widest">Connexion</div>
                <div className="text-[10px]">requise</div>
              </>
            ) : (
              <>
                <Upload size={22} />
                <div className="text-xs font-bold uppercase tracking-widest">Ajouter</div>
                <div className="text-[10px] text-[var(--ink-soft)]">{photos.length}/{MAX_PHOTOS}</div>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={onFiles}
              disabled={uploading || !user}
              data-testid="photo-file-input"
            />
          </label>
        )}
        {/* Placeholders to keep the grid balanced */}
        {Array.from({ length: Math.max(0, MAX_PHOTOS - photos.length - 1) }).map((_, i) => (
          <div
            key={`ph-${i}`}
            className="aspect-square rounded-2xl border-2 border-dashed border-[var(--line)] bg-white/40 flex flex-col items-center justify-center gap-1 text-[var(--ink-soft)]"
          >
            <Camera size={18} />
            <div className="text-[10px] uppercase tracking-widest">Photo {photos.length + i + 2}</div>
          </div>
        ))}
      </div>

      {uploadError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-xl mt-3" data-testid="photo-upload-error">
          {uploadError}
          {!isPro && uploadError.includes("Premium") && (
            <Link to="/choisir-abonnement?next=%2Fdeposer" className="ml-2 font-bold underline text-orange-700" data-testid="photo-upgrade-link">Passer Premium (2 000 FCFA/mois)</Link>
          )}
        </div>
      )}
      {!isPro && (
        <div className="text-xs text-[var(--ink-soft)] mt-2" data-testid="photo-quota-hint">
          Abonnement Gratuit : {MAX_PHOTOS} photos max · <Link to="/choisir-abonnement?next=%2Fdeposer" className="text-orange-600 font-semibold">Premium = 6 photos</Link>
        </div>
      )}
      {photos.length === 0 && !uploadError && (
        <div className="text-xs text-[var(--ink-soft)] mt-3" data-testid="photo-hint">
          Aucune photo ajoutee. La premiere photo sera utilisee comme image principale.
        </div>
      )}
    </div>
  );
}


function AutoVerifiedCheck({ form }) {
  const totalPhotos = (form.image ? 1 : 0) + (form.images?.length || 0);
  const descLen = (form.description || "").trim().length;
  const waDigits = (form.whatsapp || "").replace(/\D/g, "").length;

  const checks = [
    { ok: totalPhotos >= 3, label: `Au moins 3 photos (${totalPhotos}/3)` },
    { ok: descLen >= 30, label: `Description detaillee (${descLen}/30 caracteres min.)` },
    { ok: waDigits >= 8, label: `Numero WhatsApp renseigne (${waDigits}/8 chiffres min.)` },
  ];
  const allOk = checks.every((c) => c.ok);

  return (
    <div
      className={`mt-6 p-4 rounded-2xl border ${
        allOk
          ? "bg-green-50 border-green-200"
          : "bg-orange-50/60 border-orange-200"
      }`}
      data-testid="auto-verified-check"
    >
      <div className="flex items-center gap-2 font-bold mb-2">
        {allOk ? (
          <>
            <ShieldCheck size={18} className="text-green-600" />
            <span className="text-green-700">Votre annonce sera marquee &laquo; Verifiee &raquo;</span>
          </>
        ) : (
          <>
            <ShieldAlert size={18} className="text-orange-600" />
            <span className="text-orange-700">Pour obtenir le badge &laquo; Verifiee &raquo;</span>
          </>
        )}
      </div>
      <ul className="text-sm space-y-1">
        {checks.map((c) => (
          <li key={c.label} className="flex items-center gap-2" data-testid={`av-check-${c.ok ? "ok" : "ko"}`}>
            <CheckCircle2 size={14} className={c.ok ? "text-green-600" : "text-[var(--ink-soft)] opacity-50"} />
            <span className={c.ok ? "text-[var(--ink)]" : "text-[var(--ink-soft)]"}>{c.label}</span>
          </li>
        ))}
      </ul>
      {!allOk && (
        <p className="text-xs text-[var(--ink-soft)] mt-2">
          Le badge &laquo; Verifiee &raquo; rassure les acheteurs et augmente les chances de vente.
        </p>
      )}
    </div>
  );
}
